// .fmavatar（zip）読み込み（SPEC_FMAVATAR.md）。
// fflate で展開 → manifest.json / face.json / materials.json / meshes.bin を検証しつつパース。

import { unzipSync } from "fflate";
import {
  FMAVATAR_FORMAT_VERSION,
  LIMITS,
  VISEME_KEYS,
  type AlphaMode,
  type ColorSpace,
  type CullMode,
  type ExistingClip,
  type FaceMeshInfo,
  type FmAvatarManifest,
  type FmFaceFile,
  type FmMaterial,
  type FmMaterialsFile,
  type LoadedAvatar,
  type RenderingMode,
  type SpecialShapes,
  type TextureRef,
  type VisemeKey,
} from "./types.ts";
import { fail } from "./errors.ts";
import { parseMeshesBin } from "./meshbin.ts";

const decoder = new TextDecoder("utf-8");

// --- 小さな検証ヘルパ -------------------------------------------------------

type Obj = Record<string, unknown>;

function asObject(v: unknown, where: string, path: string): Obj {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    fail(where, `${path} がオブジェクトではありません。`);
  }
  return v as Obj;
}

function asArray(v: unknown, where: string, path: string): unknown[] {
  if (!Array.isArray(v)) fail(where, `${path} が配列ではありません。`);
  return v;
}

function reqString(o: Obj, key: string, where: string, path: string): string {
  const v = o[key];
  if (typeof v !== "string" || v.length === 0) {
    fail(where, `${path}.${key} が空でない文字列ではありません。`);
  }
  return v;
}

function optString(o: Obj, key: string, where: string, path: string): string | undefined {
  const v = o[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v !== "string") fail(where, `${path}.${key} が文字列ではありません。`);
  return v;
}

function nullableString(o: Obj, key: string, where: string, path: string): string | null {
  const v = o[key];
  if (v === undefined || v === null) return null;
  if (typeof v !== "string") fail(where, `${path}.${key} が文字列でも null でもありません。`);
  return v;
}

function reqNumber(o: Obj, key: string, where: string, path: string): number {
  const v = o[key];
  if (typeof v !== "number" || !Number.isFinite(v)) {
    fail(where, `${path}.${key} が有限の数値ではありません。`);
  }
  return v;
}

function reqInt(o: Obj, key: string, where: string, path: string): number {
  const v = reqNumber(o, key, where, path);
  if (!Number.isInteger(v)) fail(where, `${path}.${key} が整数ではありません（${v}）。`);
  return v;
}

function optNumber(o: Obj, key: string, fallback: number, where: string, path: string): number {
  if (o[key] === undefined || o[key] === null) return fallback;
  return reqNumber(o, key, where, path);
}

function optBool(o: Obj, key: string, fallback: boolean, where: string, path: string): boolean {
  if (o[key] === undefined || o[key] === null) return fallback;
  const v = o[key];
  if (typeof v !== "boolean") fail(where, `${path}.${key} が真偽値ではありません。`);
  return v;
}

function reqVec(o: Obj, key: string, n: number, where: string, path: string): number[] {
  const arr = asArray(o[key], where, `${path}.${key}`);
  if (arr.length !== n) fail(where, `${path}.${key} の要素数が ${n} ではありません（${arr.length}）。`);
  return arr.map((x, i) => {
    if (typeof x !== "number" || !Number.isFinite(x)) {
      fail(where, `${path}.${key}[${i}] が有限の数値ではありません。`);
    }
    return x;
  });
}

function optVec2(o: Obj, key: string, where: string, path: string): [number, number] | undefined {
  if (o[key] === undefined || o[key] === null) return undefined;
  const v = reqVec(o, key, 2, where, path);
  return [v[0], v[1]];
}

function enumOf<T extends string>(
  value: string, allowed: readonly T[], where: string, path: string,
): T {
  if (!(allowed as readonly string[]).includes(value)) {
    fail(where, `${path} の値 "${value}" は許可されていません（${allowed.join(" / ")}）。`);
  }
  return value as T;
}

/** JSON をパースする。NaN/Infinity は JSON.parse が受け付けないため自動的にエラーになる。 */
function parseJson(bytes: Uint8Array, where: string): unknown {
  let text: string;
  try {
    text = decoder.decode(bytes);
  } catch {
    fail(where, "UTF-8 として読めませんでした。");
  }
  try {
    return JSON.parse(text) as unknown;
  } catch (e) {
    fail(where, `JSON として解析できませんでした: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// --- zip -------------------------------------------------------------------

function isUnsafeEntryPath(name: string): boolean {
  if (name.startsWith("/") || name.startsWith("\\")) return true;
  if (/^[A-Za-z]:/.test(name)) return true;
  return name.split(/[\\/]/).some((seg) => seg === "..");
}

// --- 各ファイルのパース -----------------------------------------------------

export function parseManifest(bytes: Uint8Array): FmAvatarManifest {
  const where = "manifest.json";
  const o = asObject(parseJson(bytes, where), where, "manifest");
  const formatVersion = reqInt(o, "formatVersion", where, "manifest");
  if (formatVersion !== FMAVATAR_FORMAT_VERSION) {
    fail(
      where,
      `formatVersion ${formatVersion} はこのバージョンの FaceMaker では読み込めません（対応: ${FMAVATAR_FORMAT_VERSION}）。`,
      formatVersion > FMAVATAR_FORMAT_VERSION
        ? "Web 版 FaceMaker が古い可能性があります。ページを再読み込みして更新してください。"
        : "Unity 側の FaceMaker エクスポータを新しいバージョンに更新して再エクスポートしてください。",
    );
  }
  const manifest: FmAvatarManifest = {
    formatVersion,
    avatarName: reqString(o, "avatarName", where, "manifest"),
    createdAt: reqString(o, "createdAt", where, "manifest"),
  };
  const toolVersion = optString(o, "toolVersion", where, "manifest");
  if (toolVersion !== undefined) manifest.toolVersion = toolVersion;
  const avatarUid = optString(o, "avatarUid", where, "manifest");
  if (avatarUid !== undefined) manifest.avatarUid = avatarUid;
  const unityVersion = optString(o, "unityVersion", where, "manifest");
  if (unityVersion !== undefined) manifest.unityVersion = unityVersion;
  if (o["stats"] !== undefined && o["stats"] !== null) {
    const s = asObject(o["stats"], where, "manifest.stats");
    const stats: Record<string, number> = {};
    for (const k of ["meshCount", "totalVertexCount", "totalBlendshapeCount", "materialCount", "textureCount"]) {
      if (typeof s[k] === "number") stats[k] = s[k] as number;
    }
    manifest.stats = stats;
  }
  return manifest;
}

function parseTextureRef(v: unknown, where: string, path: string): TextureRef {
  const o = asObject(v, where, path);
  const width = reqInt(o, "width", where, path);
  const height = reqInt(o, "height", where, path);
  if (width < LIMITS.textureMinPx || width > LIMITS.textureMaxPx ||
      height < LIMITS.textureMinPx || height > LIMITS.textureMaxPx) {
    fail(where, `${path} のサイズ ${width}x${height} が許容範囲（${LIMITS.textureMinPx}〜${LIMITS.textureMaxPx}px）外です。`);
  }
  const ref: TextureRef = {
    file: reqString(o, "file", where, path),
    width,
    height,
    colorSpace: enumOf(reqString(o, "colorSpace", where, path), ["sRGB", "Linear"] as const, where, `${path}.colorSpace`) as ColorSpace,
  };
  const uvScale = optVec2(o, "uvScale", where, path);
  if (uvScale) ref.uvScale = uvScale;
  const uvOffset = optVec2(o, "uvOffset", where, path);
  if (uvOffset) ref.uvOffset = uvOffset;
  return ref;
}

const VALID_MODE_PAIRS: ReadonlyArray<[RenderingMode, AlphaMode]> = [
  ["Opaque", "Opaque"],
  ["Cutout", "AlphaClip"],
  ["Transparent", "AlphaBlend"],
];

export function parseMaterials(bytes: Uint8Array, warnings: string[]): FmMaterialsFile {
  const where = "materials.json";
  const root = asObject(parseJson(bytes, where), where, "materials.json");
  const formatVersion = reqInt(root, "formatVersion", where, "materials.json");
  const list = asArray(root["materials"], where, "materials");
  const materials: FmMaterial[] = [];
  const seen = new Set<number>();

  list.forEach((raw, i) => {
    const path = `materials[${i}]`;
    const o = asObject(raw, where, path);
    const index = reqInt(o, "index", where, path);
    if (index < 0) fail(where, `${path}.index が負の値です（${index}）。`);
    if (seen.has(index)) fail(where, `${path}.index=${index} が重複しています。`);
    seen.add(index);

    const shaderType = reqString(o, "shaderType", where, path);
    if (shaderType !== "liltoon") {
      warnings.push(`マテリアル "${o["name"]}" のシェーダー種別が "${shaderType}" です。近似シェーディングで表示します。`);
    }

    const renderingMode = enumOf(
      reqString(o, "renderingMode", where, path),
      ["Opaque", "Cutout", "Transparent"] as const, where, `${path}.renderingMode`,
    ) as RenderingMode;
    const alphaMode = enumOf(
      reqString(o, "alphaMode", where, path),
      ["Opaque", "AlphaClip", "AlphaBlend"] as const, where, `${path}.alphaMode`,
    ) as AlphaMode;
    if (!VALID_MODE_PAIRS.some(([r, a]) => r === renderingMode && a === alphaMode)) {
      fail(where, `${path}: renderingMode="${renderingMode}" と alphaMode="${alphaMode}" の組み合わせは許可されていません。`);
    }

    const texObj = asObject(o["textures"], where, `${path}.textures`);
    const textures: FmMaterial["textures"] = {
      mainTex: parseTextureRef(texObj["mainTex"], where, `${path}.textures.mainTex`),
    };
    for (const slot of ["shadeTex", "outlineMask", "normalMap"] as const) {
      if (texObj[slot] !== undefined && texObj[slot] !== null) {
        textures[slot] = parseTextureRef(texObj[slot], where, `${path}.textures.${slot}`);
      }
    }

    const p = asObject(o["properties"], where, `${path}.properties`);
    const v4 = (k: string): [number, number, number, number] => {
      const a = reqVec(p, k, 4, where, `${path}.properties`);
      return [a[0], a[1], a[2], a[3]];
    };
    materials.push({
      index,
      name: reqString(o, "name", where, path),
      shaderType,
      renderingMode,
      alphaMode,
      textures,
      properties: {
        mainColor: v4("mainColor"),
        shadeColor: v4("shadeColor"),
        shadowBorder: optNumber(p, "shadowBorder", 0.5, where, `${path}.properties`),
        shadowBlur: optNumber(p, "shadowBlur", 0.1, where, `${path}.properties`),
        outlineColor: v4("outlineColor"),
        outlineWidth: optNumber(p, "outlineWidth", 0.08, where, `${path}.properties`),
        alphaCutoff: optNumber(p, "alphaCutoff", 0.5, where, `${path}.properties`),
        // 旧ファイル（formatVersion 2 でも zWrite 追加前）との互換のため、
        // 未指定時は renderingMode から Unity 既定の ZWrite 挙動を推定する
        // （Opaque/Cutout は ZWrite On、Transparent は ZWrite Off が一般的）。
        zWrite: optBool(p, "zWrite", renderingMode !== "Transparent", where, `${path}.properties`),
        colorWrite: optBool(p, "colorWrite", true, where, `${path}.properties`),
        cull: enumOf(
          optString(p, "cull", where, `${path}.properties`) ?? "Back",
          ["Off", "Front", "Back"] as const, where, `${path}.properties.cull`,
        ) as CullMode,
      },
    });
  });

  return { formatVersion, materials };
}

export function parseFace(bytes: Uint8Array, warnings: string[]): FmFaceFile {
  const where = "face.json";
  const root = asObject(parseJson(bytes, where), where, "face.json");
  const formatVersion = reqInt(root, "formatVersion", where, "face.json");

  const meshes: FaceMeshInfo[] = asArray(root["meshes"], where, "meshes").map((raw, i) => {
    const path = `meshes[${i}]`;
    const o = asObject(raw, where, path);
    const submeshMaterials = asArray(o["submeshMaterials"], where, `${path}.submeshMaterials`).map((x, j) => {
      if (typeof x !== "number" || !Number.isInteger(x)) {
        fail(where, `${path}.submeshMaterials[${j}] が整数ではありません。`);
      }
      return x;
    });
    const blendshapeNames = asArray(o["blendshapeNames"], where, `${path}.blendshapeNames`).map((x, j) => {
      if (typeof x !== "string") fail(where, `${path}.blendshapeNames[${j}] が文字列ではありません。`);
      return x;
    });
    return {
      index: reqInt(o, "index", where, path),
      name: reqString(o, "name", where, path),
      hierarchyPath: optString(o, "hierarchyPath", where, path) ?? "",
      submeshMaterials,
      blendshapeNames,
    };
  });
  if (meshes.length === 0) fail(where, "meshes が空です。表情に使えるメッシュがありません。");

  // specialShapes
  const ss = asObject(root["specialShapes"] ?? {}, where, "specialShapes");
  const visemesRaw = asObject(ss["visemes"] ?? {}, where, "specialShapes.visemes");
  const visemes = {} as Record<VisemeKey, string | null>;
  for (const k of VISEME_KEYS) {
    visemes[k] = nullableString(visemesRaw, k, where, "specialShapes.visemes");
  }
  const blinkRaw = asObject(ss["blink"] ?? {}, where, "specialShapes.blink");
  const specialShapes: SpecialShapes = {
    visemes,
    blink: {
      left: nullableString(blinkRaw, "left", where, "specialShapes.blink"),
      right: nullableString(blinkRaw, "right", where, "specialShapes.blink"),
      combined: nullableString(blinkRaw, "combined", where, "specialShapes.blink"),
    },
  };

  // existingClips
  const existingClips: ExistingClip[] = [];
  const clipsRaw = root["existingClips"] === undefined ? [] : asArray(root["existingClips"], where, "existingClips");
  clipsRaw.forEach((raw, i) => {
    const path = `existingClips[${i}]`;
    const o = asObject(raw, where, path);
    const type = optString(o, "type", where, path) ?? "static";
    if (type !== "static") {
      // §7: 未知 type は警告のうえ無視（前方互換）
      warnings.push(`既存クリップ "${o["name"]}" は未対応の type "${type}" のため読み飛ばしました。`);
      return;
    }
    const blendshapes = (o["blendshapes"] === undefined ? [] : asArray(o["blendshapes"], where, `${path}.blendshapes`))
      .map((b, j) => {
        const bo = asObject(b, where, `${path}.blendshapes[${j}]`);
        return {
          meshIndex: reqInt(bo, "meshIndex", where, `${path}.blendshapes[${j}]`),
          name: reqString(bo, "name", where, `${path}.blendshapes[${j}]`),
          value: reqNumber(bo, "value", where, `${path}.blendshapes[${j}]`),
        };
      });
    const clip: ExistingClip = {
      index: reqInt(o, "index", where, path),
      name: reqString(o, "name", where, path),
      type,
      blendshapes,
    };
    const guid = optString(o, "assetGuid", where, path);
    if (guid !== undefined) clip.assetGuid = guid;
    existingClips.push(clip);
  });

  // existingGestures
  const egRaw = asObject(root["existingGestures"] ?? {}, where, "existingGestures");
  const readHand = (key: "left" | "right") =>
    (egRaw[key] === undefined ? [] : asArray(egRaw[key], where, `existingGestures.${key}`)).map((raw, i) => {
      const path = `existingGestures.${key}[${i}]`;
      const o = asObject(raw, where, path);
      const gesture = reqInt(o, "gesture", where, path);
      if (gesture < 0 || gesture > 7) fail(where, `${path}.gesture(${gesture}) が 0〜7 の範囲外です。`);
      return { gesture, clipRef: reqInt(o, "clipRef", where, path) };
    });
  const combos = (egRaw["combos"] === undefined ? [] : asArray(egRaw["combos"], where, "existingGestures.combos"))
    .map((raw, i) => {
      const path = `existingGestures.combos[${i}]`;
      const o = asObject(raw, where, path);
      return {
        left: reqInt(o, "left", where, path),
        right: reqInt(o, "right", where, path),
        clipRef: reqInt(o, "clipRef", where, path),
      };
    });

  // menuCandidates
  const menuCandidates = (root["menuCandidates"] === undefined ? [] : asArray(root["menuCandidates"], where, "menuCandidates"))
    .map((raw, i) => {
      const path = `menuCandidates[${i}]`;
      const o = asObject(raw, where, path);
      const clipRef = o["clipRef"];
      return {
        label: reqString(o, "label", where, path),
        clipRef: typeof clipRef === "number" ? clipRef : null,
      };
    });

  return {
    formatVersion,
    meshes,
    specialShapes,
    existingGestures: { left: readHand("left"), right: readHand("right"), combos },
    existingClips,
    menuCandidates,
  };
}

// --- 相互参照の検証（SPEC §8） ---------------------------------------------

export function crossValidate(
  face: FmFaceFile,
  materials: FmMaterialsFile,
  meshes: ReturnType<typeof parseMeshesBin>,
  warnings: string[],
): void {
  const where = ".fmavatar";

  // §8-3: face.json の meshes[].index と meshes.bin の meshIndex が過不足なく対応
  const binIndices = new Set(meshes.blocks.map((b) => b.meshIndex));
  const faceIndices = new Set(face.meshes.map((m) => m.index));
  for (const idx of faceIndices) {
    if (!binIndices.has(idx)) {
      fail(where, `face.json のメッシュ index=${idx} に対応するメッシュが meshes.bin にありません。`);
    }
  }
  for (const idx of binIndices) {
    if (!faceIndices.has(idx)) {
      fail(where, `meshes.bin のメッシュ meshIndex=${idx} が face.json に定義されていません。`);
    }
  }
  if (faceIndices.size !== face.meshes.length) {
    fail(where, "face.json の meshes[].index に重複があります。");
  }

  const matIndices = new Set(materials.materials.map((m) => m.index));
  for (const m of face.meshes) {
    const block = meshes.blocks.find((b) => b.meshIndex === m.index);
    if (!block) continue; // 上でチェック済み

    // §8-5: blendshapeNames の要素数と blendshapeCount が一致
    if (m.blendshapeNames.length !== block.blendshapeCount) {
      fail(
        where,
        `メッシュ "${m.name}"(index=${m.index}) の blendshapeNames の数(${m.blendshapeNames.length})が meshes.bin の blendshapeCount(${block.blendshapeCount})と一致しません。`,
      );
    }
    // §8-4: submeshMaterials の参照先が実在
    for (const mi of m.submeshMaterials) {
      if (!matIndices.has(mi)) {
        fail(where, `メッシュ "${m.name}" の submeshMaterials が存在しないマテリアル index=${mi} を参照しています。`);
      }
    }
    if (m.submeshMaterials.length !== block.submeshCount) {
      warnings.push(
        `メッシュ "${m.name}" の submeshMaterials 数(${m.submeshMaterials.length})が meshes.bin の submeshCount(${block.submeshCount})と異なります。不足分は先頭マテリアルで代用します。`,
      );
    }
  }

  // シェイプキー総数の警告は撤去した。Web プレビューは array texture の layer 方向に
  // シェイプキーを積む Three.js 標準 morph 機構をやめ、2D デルタテクスチャの height
  // 方向に積む自前実装にしたため、MAX_ARRAY_TEXTURE_LAYERS(保証値 256)には縛られない。
  // 実際の上限（MAX_TEXTURE_SIZE）チェックは WebGL コンテキストを持つ viewer 側で行う。

  // 既存クリップの参照先シェイプが実在するか（致命的でないため警告）
  const nameSets = new Map(face.meshes.map((m) => [m.index, new Set(m.blendshapeNames)]));
  for (const clip of face.existingClips) {
    for (const bs of clip.blendshapes) {
      const set = nameSets.get(bs.meshIndex);
      if (!set || !set.has(bs.name)) {
        warnings.push(`既存クリップ "${clip.name}" が存在しないシェイプキー "${bs.name}"(mesh ${bs.meshIndex}) を参照しています。`);
      }
    }
  }
}

// --- エントリポイント -------------------------------------------------------

export function loadFmAvatar(zipBytes: Uint8Array): LoadedAvatar {
  const where = ".fmavatar";
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(zipBytes);
  } catch (e) {
    fail(where, `zip として展開できませんでした: ${e instanceof Error ? e.message : String(e)}`, "ファイルが破損しているか、.fmavatar ではない可能性があります。");
  }

  let totalBytes = 0;
  for (const [name, data] of Object.entries(files)) {
    if (isUnsafeEntryPath(name)) {
      fail(where, `安全でないファイルパスが含まれています: "${name}"`, "信頼できる Unity 拡張から書き出したファイルを使ってください。");
    }
    totalBytes += data.byteLength;
  }
  if (totalBytes > LIMITS.totalUncompressedBytes) {
    fail(where, `展開後の合計サイズ(${(totalBytes / 1024 / 1024).toFixed(0)}MB)が上限 1GB を超えています。`);
  }

  const get = (name: string): Uint8Array => {
    const f = files[name];
    if (!f) fail(where, `必須ファイル "${name}" が見つかりません。`, "Unity 側で正常にエクスポートできているか確認してください。");
    return f;
  };

  const warnings: string[] = [];
  const manifest = parseManifest(get("manifest.json"));
  const face = parseFace(get("face.json"), warnings);
  const materials = parseMaterials(get("materials.json"), warnings);

  const binBytes = get("meshes.bin");
  const binBuffer = binBytes.buffer.slice(
    binBytes.byteOffset,
    binBytes.byteOffset + binBytes.byteLength,
  ) as ArrayBuffer;
  const meshes = parseMeshesBin(binBuffer);

  crossValidate(face, materials, meshes, warnings);

  // テクスチャ収集（参照されているもののみ。欠落は警告にとどめ、描画側で代替する）
  const textures = new Map<string, Uint8Array>();
  for (const m of materials.materials) {
    for (const ref of Object.values(m.textures)) {
      if (!ref) continue;
      const data = files[ref.file];
      if (!data) {
        warnings.push(`マテリアル "${m.name}" のテクスチャ "${ref.file}" が zip 内に見つかりません。代替色で表示します。`);
        continue;
      }
      textures.set(ref.file, data);
    }
  }

  return {
    id: crypto.randomUUID(),
    manifest,
    face,
    materials,
    meshes,
    textures,
    warnings,
    loadedAt: new Date().toISOString(),
  };
}
